/**
 * Global Toast Store
 *
 * Zustand store for managing toast notifications globally across all components.
 * This ensures that toasts triggered anywhere in the app (including inside hooks
 * like useCopyToClipboard) are visible to the Toast component in the App.
 */

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'prep' | 'return';
export type ToastPosition = 'bottom-right' | 'bottom-center';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastState {
  message: string;
  isVisible: boolean;
  type: ToastType;
  duration: number;
  action?: ToastAction;
  position: ToastPosition;
}

interface ToastStore extends ToastState {
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    action?: ToastAction,
    position?: ToastPosition
  ) => void;
  hideToast: () => void;
}

// Store timeout ID outside of Zustand state to avoid re-renders
let toastTimeout: NodeJS.Timeout | null = null;

export const useToastStore = create<ToastStore>((set) => ({
  // Initial state
  message: '',
  isVisible: false,
  type: 'info',
  duration: 3000,
  position: 'bottom-right',

  // Actions
  showToast: (
    message: string,
    type: ToastType = 'info',
    duration: number = 3000,
    action?: ToastAction,
    position: ToastPosition = 'bottom-right'
  ) => {
    // Clear any pending timeout from previous toast
    if (toastTimeout) {
      clearTimeout(toastTimeout);
      toastTimeout = null;
    }

    set({
      message,
      isVisible: true,
      type,
      duration,
      action,
      position,
    });

    // Auto-hide after duration (if duration > 0)
    if (duration > 0) {
      toastTimeout = setTimeout(() => {
        set((state) => ({ ...state, isVisible: false }));
        toastTimeout = null;
      }, duration);
    }
  },

  hideToast: () => {
    // Clear any pending timeout when manually hiding
    if (toastTimeout) {
      clearTimeout(toastTimeout);
      toastTimeout = null;
    }
    set((state) => ({ ...state, isVisible: false }));
  },
}));
